import type { PayloadAction } from '@reduxjs/toolkit';
import type { AddContactInfoRequest } from '@/types/entities/contactInfo';
import type { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import { DeleteContactPayload } from '@/domains/contacts/sagas/types';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { refreshContactList } from '@/features/entityDetails/store/entityDetailsSlice';
import { resetGeographyState } from '@/features/geography/store/geographySlice';
import { resetAddressValidationState } from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import { resetGooglePlacesState } from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { getClaimSetV2 } from '@/domains/claimSets/store/claimSetSlice';
import ContactEvents from '@/domains/contacts/events';
import { addContactAddress, addContactContactInfo, deleteContact } from '@/domains/contacts/store/contactSlice';
import { dispatchUpdatedEntityEvent } from '@/domains/contacts/utils/dispatchUpdatedEntityEvent';
import { FAILED_TO_SAVE } from '@/constants/texts';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* deleteContactSaga({ payload: { contactId, entityType, entityKey } }: PayloadAction<DeleteContactPayload>) {
  try {
    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    yield put(setSystemLoadingStatus(true));
    const { data }: HttpServiceResponse<string> = yield call(HttpService.delete, `/examiner/contacts/${contactId}`);

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    if (currentClaimSetId) {
      yield put(getClaimSetV2(String(currentClaimSetId)));
    }

    dispatchUpdatedEntityEvent({
      claimSetId: currentClaimSetId,
      entityType,
      entityKey,
      eventType: ContactEvents.ContactDeleted,
    });

    yield put(refreshContactList());
  } catch (error) {
    console.error(error);
    SnacksService.add({ type: SnackType.Error, title: FAILED_TO_SAVE });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

function* addContactAddressSaga({ payload }: PayloadAction<AddAddressRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/contacts/add-address`, payload);
    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(refreshContactList());
  } catch (error) {
    console.error(error);
    SnacksService.add({ type: SnackType.Error, title: FAILED_TO_SAVE });
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetGeographyState());
    yield put(resetAddressValidationState());
    yield put(resetGooglePlacesState());
    ModalService.close(Modals.AddAddress);
  }
}

function* addContactContactInfoSaga({ payload }: PayloadAction<AddContactInfoRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/contacts/add-contact-information`, payload);
    yield put(refreshContactList());
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.AddContactInfo);
  }
}

export function* contactWatcher() {
  yield takeLatest(deleteContact.type, deleteContactSaga);
  yield takeLatest(addContactAddress.type, addContactAddressSaga);
  yield takeLatest(addContactContactInfo.type, addContactContactInfoSaga);
}
